// kfe-bar-chart.js — hand-rolled inline-SVG bar chart (Brief 097).
//
// PORT TARGET: Classes/KFEModules/Reports/ChartViews/KFEBarChartView (iOS).
//
// No Chart.js, no D3 — a direct SVG with positions computed from the data,
// same lean-on-the-platform posture as the squiggle (CLAUDE.md two-runtime-
// deps rule). Used by the Lesson Report's "Report on Activity" section for
// minutes-per-category.
//
// Props:
//   bars:   Array<{ label: string, value: number, color?: string }>
//   yLabel: string   (optional axis caption, rendered rotated at the left)
//   maxY:   number   (optional fixed Y-max; otherwise computed per-chart with
//                     headroom — iOS uses a per-lesson computed scale, see
//                     Brief 097 gotchas / May overseer doc §8)
//
// Renders: one bar per entry, the value label above each bar, the category
// label below each bar (word-wrapped for narrow widths), and an optional
// rotated Y-axis caption. Responsive via viewBox; readable at 360px width.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";

const NS = "http://www.w3.org/2000/svg";

// viewBox geometry. The chart scales to its container width via the viewBox;
// these are abstract units chosen so text is legible when the column is
// phone-width (~360–480px).
const VB_W = 320;
const VB_H = 220;
const PAD_LEFT = 30;   // room for the rotated Y caption
const PAD_RIGHT = 10;
const PAD_TOP = 24;    // room for value labels above the tallest bar
const PAD_BOTTOM = 44; // room for (possibly two-line) category labels
const PLOT_X = PAD_LEFT;
const PLOT_W = VB_W - PAD_LEFT - PAD_RIGHT;
const PLOT_TOP = PAD_TOP;
const BASELINE = VB_H - PAD_BOTTOM;
const PLOT_H = BASELINE - PLOT_TOP;

/** @param {SVGElement} parent @param {string} tag @param {Record<string,string>} attrs */
function el(parent, tag, attrs) {
  const node = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  parent.appendChild(node);
  return node;
}

export class KFEBarChart extends KFEComponent {
  static props = ["bars", "yLabel", "maxY"];
  static styles = `
    :host { display: block; }
    svg { width: 100%; height: auto; display: block; }
    .axis { stroke: var(--kfe-border); stroke-width: 1; }
    .bar-label { fill: var(--kfe-fg); font-size: 11px; font-weight: 600; text-anchor: middle; }
    .cat-label { fill: var(--kfe-muted); font-size: 9px; text-anchor: middle; }
    .y-caption { fill: var(--kfe-muted); font-size: 10px; text-anchor: middle; }
  `;

  render() {
    const bars = /** @type {{label:string,value:number,color?:string}[]} */ (
      /** @type {any} */ (this).bars
    ) ?? [];
    const yLabel = /** @type {string|undefined} */ (/** @type {any} */ (this).yLabel);
    const maxYProp = Number(/** @type {any} */ (this).maxY);

    const svg = document.createElementNS(NS, "svg");
    svg.setAttribute("viewBox", `0 0 ${VB_W} ${VB_H}`);
    svg.setAttribute("role", "img");
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

    // Baseline axis is always drawn so an empty chart still reads as a chart
    // rather than a blank box (no crash on empty array).
    el(svg, "line", {
      class: "axis",
      x1: String(PLOT_X), y1: String(BASELINE),
      x2: String(PLOT_X + PLOT_W), y2: String(BASELINE),
    });

    if (yLabel) {
      const cy = PLOT_TOP + PLOT_H / 2;
      el(svg, "text", {
        class: "y-caption",
        x: "10", y: String(cy),
        transform: `rotate(-90 10 ${cy})`,
      }).textContent = yLabel;
    }

    if (bars.length > 0) {
      const values = bars.map((b) => (Number.isFinite(b.value) ? b.value : 0));
      // Per-chart computed scale with ~12% headroom (iOS canon, May overseer
      // doc §8), unless an explicit maxY is supplied.
      const computed = Math.max(...values, 0);
      const maxY = Number.isFinite(maxYProp) && maxYProp > 0
        ? maxYProp
        : (computed > 0 ? computed * 1.12 : 1);

      const slot = PLOT_W / bars.length;
      const bw = slot * 0.6;
      bars.forEach((b, i) => {
        const value = values[i];
        const cx = PLOT_X + slot * i + slot / 2;
        const x = cx - bw / 2;
        const bh = maxY > 0 ? (value / maxY) * PLOT_H : 0;
        const y = BASELINE - bh;
        el(svg, "rect", {
          class: "bar",
          x: x.toFixed(2), y: y.toFixed(2),
          width: bw.toFixed(2), height: Math.max(0, bh).toFixed(2),
          rx: "2",
          fill: b.color || "var(--kfe-accent)",
        });
        // Value label above the bar.
        el(svg, "text", {
          class: "bar-label",
          x: cx.toFixed(2), y: (y - 5).toFixed(2),
        }).textContent = String(formatValue(value));
        // Category label below the baseline, word-wrapped.
        this._renderCatLabel(svg, b.label, cx);
      });
    }

    return html`${svg}`;
  }

  /** Word-wrap a category label into up to two stacked tspans under the bar.
   * @param {SVGElement} svg @param {string} label @param {number} cx */
  _renderCatLabel(svg, label, cx) {
    const text = document.createElementNS(NS, "text");
    text.setAttribute("class", "cat-label");
    text.setAttribute("x", cx.toFixed(2));
    text.setAttribute("y", String(BASELINE + 14));
    const words = String(label).split(/\s+/).filter(Boolean);
    /** @type {string[]} */
    const lines = [];
    // Pack words two-per-line max so "Language to Body" wraps gracefully at
    // narrow widths without overflowing the slot.
    let cur = "";
    for (const w of words) {
      const next = cur ? `${cur} ${w}` : w;
      if (next.length > 9 && cur) { lines.push(cur); cur = w; }
      else cur = next;
    }
    if (cur) lines.push(cur);
    if (lines.length === 0) lines.push("");
    lines.slice(0, 2).forEach((line, i) => {
      const tspan = document.createElementNS(NS, "tspan");
      tspan.setAttribute("x", cx.toFixed(2));
      tspan.setAttribute("dy", i === 0 ? "0" : "10");
      tspan.textContent = line;
      text.appendChild(tspan);
    });
    svg.appendChild(text);
  }
}

/** Drop a needless trailing ".0" but keep one decimal for fractional values. */
function formatValue(v) {
  if (!Number.isFinite(v)) return "0";
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}
